import type {
  CoachMemoryRequestDto,
  RegisterCoachMemoryRequest,
  RegisteredCoachMemoryRequest,
  ResolveCoachMemoryRequest,
  AiCoachDraftDecision,
  AiCoachDraftMemorySuggestion,
  AiCoachDraftRequest,
  AiCoachDraftResult,
  CoachMemoryItem,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, withCredentials } from "./api";
import { commonHeaders, jsonOr401 } from "./clinician-api";

// TES-1237 — the coach drafts under the narrowed contract. QA only: with
// AI_COACH_DRAFTS_ENABLED off every route answers 404
// `ai_coach_drafts_disabled`, which the admin reads as "the feature does not
// exist here" and renders nothing.

export class CoachDraftsDisabledError extends Error {}

async function draftsJson<T>(res: Response): Promise<T> {
  if (res.status === 404) {
    let code: string | null = null;
    try {
      code = ((await res.clone().json()) as { error?: string })?.error ?? null;
    } catch {
      // not JSON
    }
    if (code === "ai_coach_drafts_disabled") {
      throw new CoachDraftsDisabledError();
    }
  }
  return jsonOr401(res);
}

// One answer per page load: the switch is per box and does not change while
// the admin is open.
let availability: Promise<boolean> | null = null;
export function coachDraftsAvailable(): Promise<boolean> {
  availability ??= apiFetch(
    `${API_BASE}/employee-api/v1/ai-assist/coach-drafts/availability`,
    { headers: commonHeaders(), ...withCredentials },
  )
    .then((res) => draftsJson<{ enabled: boolean }>(res))
    .then((r) => r.enabled === true)
    .catch(() => {
      availability = null;
      return false;
    });
  return availability;
}

export async function generateCoachDraft(
  patientId: string,
  body: AiCoachDraftRequest,
): Promise<AiCoachDraftResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/coach-drafts`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return draftsJson(res);
}

export async function decideCoachDraft(
  patientId: string,
  draftId: string,
  body: AiCoachDraftDecision,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/coach-drafts/${draftId}/decision`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  if (res.status === 204) return;
  await draftsJson(res);
}

export async function acceptCoachDraftMemory(
  patientId: string,
  draftId: string,
  body: AiCoachDraftMemorySuggestion,
): Promise<CoachMemoryItem> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/coach-drafts/${draftId}/memory`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return draftsJson(res);
}

// TES-1237 S3, TES-1367 — the patient's correction, deletion and restriction
// requests, registered by her coach. Not behind the AI switch since
// TES-1367; the 404 handling stays for a box still running an older api.
export async function listMemoryRequests(
  patientId: string,
): Promise<CoachMemoryRequestDto[]> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory-requests`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return draftsJson(res);
}

export async function registerMemoryRequest(
  patientId: string,
  body: RegisterCoachMemoryRequest,
): Promise<RegisteredCoachMemoryRequest> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory-requests`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return draftsJson(res);
}

export async function resolveMemoryRequest(
  patientId: string,
  requestId: string,
  body: ResolveCoachMemoryRequest,
): Promise<CoachMemoryRequestDto[]> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory-requests/${requestId}/resolve`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return draftsJson(res);
}
